import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { StatusBadge } from "@/components/StatusBadge"
import { ModuleCard } from "@/components/ModuleCard"
import { ProposalKindChip } from "@/components/ProposalKindChip"
import { formatEur, relativeFromNow } from "@/lib/format"
import { listProposals, type ProposalListRow } from "@/lib/api"
import { KIND_VALUES, detailPathForKind, type Kind } from "@/lib/kind-labels"

interface HomeProps {
  shell: AppShell | null
}

const MODULE_CTA: Record<Kind, string> = {
  solar: "/solar/criar",
  ac: "/ac/criar",
  bc_aqs: "/bc-aqs/criar",
  bc_aq: "/bc-aq/criar",
}

// Todos os módulos activos (criação). Mantido p/ futuros placeholders.
const MODULE_COMING_SOON_PHASE: Partial<Record<Kind, number>> = {}

export function Home({ shell }: HomeProps) {
  const [counts, setCounts] = useState<Record<Kind, number | undefined>>({
    solar: undefined,
    ac: undefined,
    bc_aqs: undefined,
    bc_aq: undefined,
  })
  const [recent, setRecent] = useState<ProposalListRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!shell) {
      setLoading(false)
      return
    }
    let cancelled = false
    // Uma só chamada (todos os kinds) — agrupar por kind no client é mais
    // eficiente que 4 round-trips. Limite alto garante contagens precisas
    // sem KPI especial do backend.
    listProposals(shell, { limit: 500 })
      .then((res) => {
        if (cancelled) return
        const grouped: Record<Kind, number> = {
          solar: 0,
          ac: 0,
          bc_aqs: 0,
          bc_aq: 0,
        }
        for (const item of res.items) {
          const k = (item.kind ?? "solar") as Kind
          if (k in grouped) grouped[k] += 1
        }
        setCounts(grouped)
        setRecent(res.items.slice(0, 5))
      })
      .catch((err) => {
        console.error("[home] load failed", err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [shell])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Mais Energia</h1>
        <p className="text-sm text-muted-foreground">
          Escolhe o módulo para criar uma nova proposta.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {KIND_VALUES.map((kind) => (
          <ModuleCard
            key={kind}
            kind={kind}
            count={loading ? undefined : counts[kind] ?? 0}
            ctaPath={MODULE_CTA[kind]}
            comingSoonPhase={MODULE_COMING_SOON_PHASE[kind]}
            viewAllPath={`/list?kind=${kind}`}
          />
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Últimas propostas</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {recent.length === 0 ? (
            <div className="px-6 py-10 text-center text-sm text-muted-foreground">
              Sem propostas ainda. Cria a primeira a partir de um módulo acima.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Número</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Preço</TableHead>
                  <TableHead>Criado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recent.map((row) => {
                  const kind = (row.kind ?? "solar") as Kind
                  // Fase 1: só Solar tem rota de detalhe — as outras ainda
                  // não devem existir, mas defensivamente bloqueamos.
                  const linkTo = detailPathForKind(kind, row.id)
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <ProposalKindChip kind={kind} />
                      </TableCell>
                      <TableCell>
                        {linkTo ? (
                          <Link
                            to={linkTo}
                            className="font-medium text-accent hover:underline"
                          >
                            {row.number}
                          </Link>
                        ) : (
                          <span className="font-medium">{row.number}</span>
                        )}
                      </TableCell>
                      <TableCell>{row.client_name}</TableCell>
                      <TableCell>
                        <StatusBadge status={row.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        {formatEur(row.total_price_eur)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {relativeFromNow(row.created_at)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
